'use client';

import { useTranslations } from 'next-intl';
import { IconButton, MetaDefault, Tooltip } from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import { Link01Icon } from '@terra/ui-icons';
import { automationId } from '@terra/shared-utils';
import { willEmptySourceEndpoint } from '../../utils/reassociate-har-entries/will-empty-source-endpoint';
import { REQUESTS_REASSOCIATE_AUTOMATION_IDS } from './requestsReassociateAutomationIds';

type RequestsReassociateActionsBarProps = {
  selectedCount: number;
  sourceHarTotalCount: number;
  onReassociate: () => void;
};

export function RequestsReassociateActionsBar({
  selectedCount,
  sourceHarTotalCount,
  onReassociate,
}: RequestsReassociateActionsBarProps) {
  const t = useTranslations('proxy.requests.reassociate');
  const tCommon = useTranslations('common');
  const hasSelection = selectedCount > 0;
  const emptiesSource = willEmptySourceEndpoint({
    selectedCount,
    sourceHarTotalCount,
  });
  const isReattachDisabled = !hasSelection || emptiesSource;
  const reattachLabel = tCommon('reattach');
  const reattachTooltip = emptiesSource
    ? t('emptiesSourceTooltip')
    : reattachLabel;

  return (
    <Box
      w="full"
      px="1"
      bg="bg.canvas"
      borderBottomWidth="1px"
      borderColor="border.subtle"
    >
      <HStack
        gap="1"
        alignItems="center"
        justifyContent="space-between"
        pl="3"
        h="31px"
      >
        <HStack
          gap="1"
          alignItems="center"
          justifyContent="flex-end"
          flexShrink={0}
          ml="auto"
        >
          {hasSelection ? (
            <MetaDefault color="fg.muted" mr="1" flexShrink={0}>
              {tCommon('selectedCount', { count: selectedCount })}
            </MetaDefault>
          ) : null}
          <Tooltip title={reattachTooltip}>
            <Box display="inline-flex" flexShrink={0}>
              <IconButton
                aria-label={reattachLabel}
                size="xs"
                variant="ghost"
                disabled={isReattachDisabled}
                onClick={isReattachDisabled ? undefined : onReassociate}
                flexShrink={0}
                {...automationId(REQUESTS_REASSOCIATE_AUTOMATION_IDS.moveAction)}
              >
                <Link01Icon size={12} />
              </IconButton>
            </Box>
          </Tooltip>
        </HStack>
      </HStack>
    </Box>
  );
}
